import { useCallback, useEffect, useMemo, useState, type CSSProperties } from 'react';
import { useNavigate } from 'react-router-dom';
import { ownerApi, type OwnerMeeting } from '@/features/workspace/api';
import { useToast } from '@/hooks/useToast';
import { routes } from '@/lib/routes';

type EventKind = 'MEETING' | 'DUTY';
type EventStatus = 'PENDING' | 'ACCEPTED' | 'DECLINED' | 'COMPLETED';

type EventItem = {
  id: string;
  kind: EventKind;
  title: string;
  recipient_label: string;
  scheduled_at: string;
  format: string;
  place: string;
  invite_status: string;
  status: EventStatus;
  agenda: string;
};

function kindLabel(kind: EventKind): string {
  return kind === 'DUTY' ? 'Дежурство' : 'Встреча';
}

function statusLabel(status: EventStatus): string {
  return (
    {
      PENDING: 'Ожидает',
      ACCEPTED: 'Принято',
      DECLINED: 'Отклонено',
      COMPLETED: 'Завершено',
    }[status] ?? status
  );
}

function normalizeStatus(status: string): EventStatus {
  const upper = status.toUpperCase();
  if (upper === 'ACCEPTED' || upper === 'DECLINED' || upper === 'COMPLETED') {
    return upper;
  }
  return 'PENDING';
}

function normalizeKind(kind: string): EventKind {
  return kind.toUpperCase() === 'DUTY' ? 'DUTY' : 'MEETING';
}

function formatScheduledAt(value: string): string {
  if (!value) return '—';
  return value.replace('T', ' ').slice(0, 16);
}

function mapMeeting(item: OwnerMeeting): EventItem {
  return {
    id: item.id,
    kind: normalizeKind(item.kind),
    title: item.title,
    recipient_label: item.recipient_label || '—',
    scheduled_at: formatScheduledAt(item.scheduled_at),
    format: 'Онлайн · ссылка на встречу',
    place: '',
    invite_status: item.invite_status || '—',
    status: normalizeStatus(item.status),
    agenda: item.agenda || '',
  };
}

export const MeetingsDutyPage = ({
  onClose,
}: {
  onClose?: () => void;
} = {}) => {
  const navigate = useNavigate();
  const { notify } = useToast();
  const [events, setEvents] = useState<EventItem[]>([]);
  const [eventType, setEventType] = useState<EventKind>('MEETING');
  const [filter, setFilter] = useState('ALL');
  const [form, setForm] = useState({
    title: '',
    recipient: '',
    when: '',
    duration: '60 мин',
    format: 'Онлайн · ссылка на встречу',
    place: '',
    message: '',
    notifyInApp: true,
    notifyEmail: true,
  });

  const loadEvents = useCallback(async () => {
    try {
      const payload = await ownerApi.module('meetings', { tab: 'registry' });
      const items = (payload.items as OwnerMeeting[] | undefined) ?? [];
      setEvents(items.map(mapMeeting));
    } catch {
      notify('Не удалось загрузить события');
    }
  }, [notify]);

  useEffect(() => {
    document.body.classList.add('owner-uat-owner-route-open');
    void loadEvents();
    return () => {
      document.body.classList.remove('owner-uat-owner-route-open');
    };
  }, [loadEvents]);

  const filtered = useMemo(() => {
    return events.filter((item) => {
      if (filter === 'ALL') return true;
      if (filter === 'MEETING' || filter === 'DUTY') return item.kind === filter;
      return item.status === filter;
    });
  }, [events, filter]);

  const kpis = [
    { label: 'Всего событий', value: events.length, accent: '#41d2ff', note: 'кабинет владельца' },
    {
      label: 'Ожидают',
      value: events.filter((item) => item.status === 'PENDING').length,
      accent: '#e2ad3a',
      note: 'ответ получателя',
    },
    {
      label: 'Принято',
      value: events.filter((item) => item.status === 'ACCEPTED').length,
      accent: '#49ddb7',
      note: 'подтверждено',
    },
    {
      label: 'Дежурства',
      value: events.filter((item) => item.kind === 'DUTY').length,
      accent: '#9e80ff',
      note: 'групповые слоты',
    },
  ];

  const save = async (send: boolean) => {
    if (!form.title.trim() || !form.when) {
      notify('Укажите тему и дату');
      return;
    }
    try {
      const created = await ownerApi.createMeeting({
        kind: eventType,
        title: form.title.trim(),
        agenda: form.message,
        scheduled_at: form.when,
        recipient_label: form.recipient.trim() || 'Получатель не указан',
        notify_in_app: form.notifyInApp,
        notify_email: form.notifyEmail,
        send_invite: send,
      });
      setEvents((prev) => [mapMeeting(created), ...prev]);
      setForm({
        title: '',
        recipient: '',
        when: '',
        duration: '60 мин',
        format: 'Онлайн · ссылка на встречу',
        place: '',
        message: '',
        notifyInApp: true,
        notifyEmail: true,
      });
      if (!send) {
        notify('Черновик сохранён без уведомлений');
        return;
      }
      if (created.notify_detail) {
        notify(
          created.notify_email_delivered || created.notify_in_app_delivered
            ? `Приглашение отправлено · ${created.notify_detail}`
            : `Приглашение сохранено · ${created.notify_detail}`,
        );
        return;
      }
      notify('Приглашение отправлено');
    } catch {
      notify('Не удалось сохранить событие');
    }
  };

  const setStatus = async (id: string, status: EventStatus) => {
    try {
      const updated = await ownerApi.updateMeetingStatus(id, status);
      setEvents((prev) =>
        prev.map((item) => (item.id === id ? mapMeeting(updated) : item)),
      );
      notify(statusLabel(status));
    } catch {
      notify('Не удалось обновить статус');
    }
  };

  const close = () => {
    if (onClose) {
      onClose();
      return;
    }
    navigate(routes.owner);
  };

  return (
    <div
      className="owner-uat-owner-overlay open"
      role="dialog"
      aria-modal="true"
      aria-label="Встреча / дежурство"
    >
      <div className="r59-route-shell">
        <header className="r59-route-top">
          <span className="owner-uat-route-icon" aria-hidden="true">
            ♛
          </span>
          <div>
            <div className="owner-uat-route-kicker">Владелец системы · Zertteu Academy</div>
            <h1>Встреча / дежурство</h1>
            <p>Встречи, дежурства, приглашения, статусы и календарная синхронизация</p>
          </div>
          <span className="spacer" />
          <button type="button" onClick={close}>
            ← Назад
          </button>
          <button type="button" onClick={close}>
            Закрыть ×
          </button>
        </header>

        <main className="r59-route-content">
      <section className="owner-uat-route-hero" style={{ '--owner-uat-accent': '#49ddb7' } as CSSProperties}>
        <div>
          <div className="owner-uat-route-kicker">Единая запись события</div>
          <h2>Встреча / дежурство</h2>
          <p>
            Встреча, дежурство, служебный календарь и уведомление получателя используют одну запись.
            Изменение времени отражается в одном состоянии и не создаёт копии даты.
          </p>
        </div>
        <button
          type="button"
          className="owner-uat-btn"
          onClick={() => navigate(routes.assistant)}
        >
          Создать через консультанта
        </button>
      </section>

      <div className="owner-uat-kpis">
        {kpis.map((kpi) => (
          <article key={kpi.label} className="owner-uat-kpi" style={{ '--kpi': kpi.accent } as CSSProperties}>
            <small>{kpi.label}</small>
            <strong>{kpi.value}</strong>
            <span>{kpi.note}</span>
          </article>
        ))}
      </div>

      <section className="owner-uat-panel">
        <header>
          <div>
            <h3>Создать событие</h3>
            <p>
              Получатель встречи отвечает «Принять / Отклонить»; дежурство можно назначить группе.
            </p>
          </div>
          <span className="owner-uat-spacer" />
          <div className="owner-uat-switch">
            <button
              type="button"
              className={`owner-uat-switch-btn${eventType === 'MEETING' ? ' active' : ''}`}
              onClick={() => setEventType('MEETING')}
            >
              Встреча
            </button>
            <button
              type="button"
              className={`owner-uat-switch-btn${eventType === 'DUTY' ? ' active' : ''}`}
              onClick={() => setEventType('DUTY')}
            >
              Дежурство
            </button>
          </div>
        </header>
        <div className="owner-uat-panel-body">
          <div className="owner-uat-grid2">
            <label style={{ display: 'grid', gap: 6 }}>
              <span style={{ color: '#8faebb', fontSize: 12 }}>Тема</span>
              <input
                className="owner-uat-input"
                value={form.title}
                onChange={(e) => setForm((prev) => ({ ...prev, title: e.target.value }))}
                placeholder={eventType === 'DUTY' ? 'Название дежурства' : 'Тема встречи'}
              />
            </label>
            <label style={{ display: 'grid', gap: 6 }}>
              <span style={{ color: '#8faebb', fontSize: 12 }}>
                {eventType === 'DUTY' ? 'Группа / участники' : 'Получатель / группа'}
              </span>
              <input
                className="owner-uat-input"
                value={form.recipient}
                onChange={(e) => setForm((prev) => ({ ...prev, recipient: e.target.value }))}
                placeholder="Пользователь, идентификатор, роль, группа или когорта…"
              />
            </label>
            <label style={{ display: 'grid', gap: 6 }}>
              <span style={{ color: '#8faebb', fontSize: 12 }}>Дата и время</span>
              <input
                className="owner-uat-input"
                type="datetime-local"
                value={form.when}
                onChange={(e) => setForm((prev) => ({ ...prev, when: e.target.value }))}
              />
            </label>
            <label style={{ display: 'grid', gap: 6 }}>
              <span style={{ color: '#8faebb', fontSize: 12 }}>
                {eventType === 'DUTY' ? 'Период / смена' : 'Продолжительность'}
              </span>
              {eventType === 'DUTY' ? (
                <input
                  className="owner-uat-input"
                  value={form.duration}
                  onChange={(e) => setForm((prev) => ({ ...prev, duration: e.target.value }))}
                  placeholder="Например: 10:00–14:00"
                />
              ) : (
                <select
                  className="owner-uat-select"
                  value={form.duration}
                  onChange={(e) => setForm((prev) => ({ ...prev, duration: e.target.value }))}
                >
                  <option>30 мин</option>
                  <option>60 мин</option>
                  <option>90 мин</option>
                </select>
              )}
            </label>
            <label style={{ display: 'grid', gap: 6 }}>
              <span style={{ color: '#8faebb', fontSize: 12 }}>Формат</span>
              <select
                className="owner-uat-select"
                value={form.format}
                onChange={(e) => setForm((prev) => ({ ...prev, format: e.target.value }))}
              >
                <option>Онлайн · ссылка на встречу</option>
                <option>Очно</option>
                <option>Телефон / другое</option>
              </select>
            </label>
            <label style={{ display: 'grid', gap: 6 }}>
              <span style={{ color: '#8faebb', fontSize: 12 }}>Ссылка / место</span>
              <input
                className="owner-uat-input"
                value={form.place}
                onChange={(e) => setForm((prev) => ({ ...prev, place: e.target.value }))}
              />
            </label>
          </div>
          <label style={{ display: 'grid', gap: 6, marginTop: 10 }}>
            <span style={{ color: '#8faebb', fontSize: 12 }}>Инструкция / сообщение</span>
            <textarea
              className="owner-uat-textarea"
              value={form.message}
              onChange={(e) => setForm((prev) => ({ ...prev, message: e.target.value }))}
            />
          </label>
          <div className="owner-uat-actions" style={{ marginTop: 10 }}>
            <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 13 }}>
              <input
                type="checkbox"
                checked={form.notifyInApp}
                onChange={(e) => setForm((prev) => ({ ...prev, notifyInApp: e.target.checked }))}
              />
              Уведомление в системе
            </label>
            <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 13 }}>
              <input
                type="checkbox"
                checked={form.notifyEmail}
                onChange={(e) => setForm((prev) => ({ ...prev, notifyEmail: e.target.checked }))}
              />
              Уведомление на email
            </label>
          </div>
          <div className="owner-uat-actions" style={{ marginTop: 12 }}>
            <button type="button" className="owner-uat-btn" onClick={() => void save(false)}>
              Сохранить черновик
            </button>
            <button type="button" className="owner-uat-btn primary" onClick={() => void save(true)}>
              Отправить приглашение
            </button>
          </div>
        </div>
      </section>

      <section className="owner-uat-panel">
        <header>
          <div>
            <h3>События и статусы</h3>
            <p>Все / Встречи / Дежурства · статус · период · получатель</p>
          </div>
          <span className="owner-uat-spacer" />
          <select className="owner-uat-select" value={filter} onChange={(e) => setFilter(e.target.value)}>
            <option value="ALL">Все</option>
            <option value="MEETING">Встречи</option>
            <option value="DUTY">Дежурства</option>
            <option value="PENDING">Ожидают</option>
            <option value="ACCEPTED">Принято</option>
            <option value="DECLINED">Отклонено</option>
            <option value="COMPLETED">Завершено</option>
          </select>
        </header>
        <div className="owner-uat-panel-body">
          <div className="owner-uat-table-wrap" style={{ overflow: 'auto' }}>
            <table className="owner-uat-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  <th style={thStyle}>Код</th>
                  <th style={thStyle}>Тип / тема</th>
                  <th style={thStyle}>Получатель</th>
                  <th style={thStyle}>Дата</th>
                  <th style={thStyle}>Формат</th>
                  <th style={thStyle}>Статус</th>
                  <th style={thStyle}>Действия</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((item) => (
                  <tr key={item.id}>
                    <td style={tdStyle}>
                      <code>{item.id}</code>
                    </td>
                    <td style={tdStyle}>
                      <b>{kindLabel(item.kind)}</b>
                      <br />
                      {item.title}
                      {item.agenda ? (
                        <>
                          <br />
                          <small style={{ color: '#8faebb' }}>{item.agenda}</small>
                        </>
                      ) : null}
                    </td>
                    <td style={tdStyle}>{item.recipient_label}</td>
                    <td style={tdStyle}>{item.scheduled_at}</td>
                    <td style={tdStyle}>
                      {item.format}
                      {item.place ? (
                        <>
                          <br />
                          <small style={{ color: '#8faebb' }}>{item.place}</small>
                        </>
                      ) : null}
                    </td>
                    <td style={tdStyle}>
                      <span
                        className="owner-uat-chip"
                        style={
                          {
                            '--chip':
                              item.status === 'ACCEPTED'
                                ? '#49ddb7'
                                : item.status === 'PENDING'
                                  ? '#e2ad3a'
                                  : '#8faebb',
                          } as CSSProperties
                        }
                      >
                        {statusLabel(item.status)}
                      </span>
                      <br />
                      <small style={{ color: '#8faebb' }}>{item.invite_status}</small>
                    </td>
                    <td style={tdStyle}>
                      <div className="owner-uat-actions" style={{ flexWrap: 'wrap' }}>
                        <button
                          type="button"
                          className="owner-uat-btn"
                          onClick={() => void setStatus(item.id, 'ACCEPTED')}
                        >
                          Принять
                        </button>
                        <button
                          type="button"
                          className="owner-uat-btn"
                          onClick={() => void setStatus(item.id, 'DECLINED')}
                        >
                          Отклонить
                        </button>
                        <button
                          type="button"
                          className="owner-uat-btn"
                          onClick={() => void setStatus(item.id, 'COMPLETED')}
                        >
                          Завершить
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {!filtered.length ? <div className="owner-uat-empty">Событий по фильтру нет.</div> : null}
        </div>
      </section>
        </main>
      </div>
    </div>
  );
};

const thStyle: CSSProperties = {
  position: 'sticky',
  top: 0,
  textAlign: 'left',
  padding: '10px 9px',
  fontSize: 9,
  textTransform: 'uppercase',
  letterSpacing: '0.08em',
  color: '#83a6b8',
  background: '#0c2839',
  borderBottom: '1px solid rgba(255,255,255,0.08)',
};

const tdStyle: CSSProperties = {
  padding: '10px 9px',
  fontSize: 11,
  verticalAlign: 'top',
  borderBottom: '1px solid rgba(255,255,255,0.075)',
};
